import React from 'react';
import { X, Plus, Minus, ShoppingCart, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { useSite } from '@/context/SiteContext';

export const CartDrawer = () => {
  const { 
    cartItems, 
    isCartOpen, 
    closeCart, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal,
    formatPrice 
  } = useCart();
  const { siteData } = useSite();

  const subtotal = getCartTotal();
  const shipping = siteData?.shipping || { flatRate: 5, freeFrom: 50 };
  const shippingCost = subtotal >= shipping.freeFrom ? 0 : shipping.flatRate;
  const total = subtotal + shippingCost;

  return (
    <Sheet open={isCartOpen} onOpenChange={closeCart}>
      <SheetContent className="flex flex-col w-full sm:max-w-lg">
        <SheetHeader className="space-y-2">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <ShoppingCart className="h-5 w-5 text-primary" />
            Carrinho de Compras
          </SheetTitle>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingCart className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">O teu carrinho está vazio</h3>
            <p className="text-muted-foreground text-sm mb-6">
              Descobre os nossos produtos e adiciona algo especial!
            </p>
            <Button asChild className="btn-festa" onClick={closeCart}>
              <Link to="/loja">Ver Produtos</Link>
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 p-3 bg-muted/30 rounded-xl">
                    <Link 
                      to={`/produto/${item.id}`} 
                      onClick={closeCart}
                      className="flex-shrink-0"
                    >
                      <img
                        src={item.images?.[0] || 'https://via.placeholder.com/80x80'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/produto/${item.id}`} 
                        onClick={closeCart}
                        className="font-medium text-foreground text-sm line-clamp-2 hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-medium text-sm">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="mt-2 font-semibold text-primary">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Portes {subtotal >= shipping.freeFrom && <span className="text-success">(Grátis!)</span>}
                  </span>
                  <span className="font-medium">
                    {shippingCost === 0 ? 'Grátis' : formatPrice(shippingCost)}
                  </span>
                </div>
                {subtotal < shipping.freeFrom && (
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-lg">
                    Faltam <strong>{formatPrice(shipping.freeFrom - subtotal)}</strong> para portes grátis!
                  </p>
                )}
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Total</span>
                <span className="text-xl font-bold text-primary">{formatPrice(total)}</span>
              </div>

              <Button 
                asChild 
                className="w-full btn-festa py-6"
                onClick={closeCart}
              >
                <Link to="/checkout">
                  Finalizar Encomenda
                </Link>
              </Button>

              <Button 
                variant="ghost" 
                className="w-full" 
                onClick={closeCart}
              >
                Continuar a Comprar
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
